/**
 * The month grid behind `Calendar` — pure, so the calendar's arithmetic is
 * assertable without rendering anything.
 *
 * Every date here is an ISO calendar day (`YYYY-MM-DD`) and every step goes
 * through UTC, never a local `Date`: `lib/epochDay.ts` explains why (a zone
 * whose DST transition lands on local midnight breaks `+1 day`). Month steps
 * cannot use epoch days — months are not a fixed number of days — so they go
 * through `Date.UTC`, which has no transitions either.
 */

import { cachedDateFormat } from '@/lib/dateLocale';
import { epochDayToIso, isoToEpochDay } from '@/lib/epochDay';

/** Weeks start on Monday (en-GB / da-DK), not Sunday. */
const DAYS_PER_WEEK = 7;

/** A grid is always 6 weeks, so the popover never changes height mid-month. */
const WEEKS_PER_GRID = 6;

/**
 * 1970-01-01 (epoch day 0) was a Thursday, which is index 3 in a Monday-first
 * week — hence the +3 before the modulo.
 */
export const weekdayIndex = (isoDate: string): number => {
  const day = isoToEpochDay(isoDate);
  if (day === null) return 0;
  return (((day + 3) % DAYS_PER_WEEK) + DAYS_PER_WEEK) % DAYS_PER_WEEK;
};

const parts = (isoDate: string): { year: number; month: number } => {
  const [year, month] = isoDate.split('-').map(Number);
  return { year: year ?? 1970, month: month ?? 1 };
};

/** The first of `isoDate`'s month. Unparsable input falls back to the epoch. */
export const startOfMonthIso = (isoDate: string): string => {
  if (isoToEpochDay(isoDate) === null) return '1970-01-01';
  const { year, month } = parts(isoDate);
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-01`;
};

/**
 * The same day-of-month `delta` months away, clamped into the target month —
 * 31 Jan + 1 month is 28 Feb, not 3 March. `Date.UTC` rolls over instead, so
 * the day is clamped against the target month's own length.
 */
export const addMonthsIso = (isoDate: string, delta: number): string => {
  if (isoToEpochDay(isoDate) === null) return isoDate;
  const { year, month } = parts(isoDate);
  const day = Number(isoDate.slice(8, 10));
  const target = new Date(Date.UTC(year, month - 1 + delta, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return target.toISOString().slice(0, 10);
};

export const isSameMonth = (isoDate: string, otherIso: string): boolean =>
  isoDate.slice(0, 7) === otherIso.slice(0, 7);

/**
 * The 6×7 grid of ISO days covering `monthIso`'s month, padded with the tail of
 * the previous month and the head of the next so every row is a full week.
 */
export const monthGrid = (monthIso: string): string[][] => {
  const first = startOfMonthIso(monthIso);
  const firstDay = isoToEpochDay(first);
  if (firstDay === null) return [];
  const gridStart = firstDay - weekdayIndex(first);

  return Array.from({ length: WEEKS_PER_GRID }, (_, week) =>
    Array.from({ length: DAYS_PER_WEEK }, (_, weekday) =>
      epochDayToIso(gridStart + week * DAYS_PER_WEEK + weekday),
    ),
  );
};

/*
  Labels are formatted in UTC for the same reason as `lib/format.ts`: a plan
  date is a calendar day with no timezone to convert. Instances live at module
  scope — `Intl` construction is expensive and this runs per rendered day —
  one per UI language, since day and month names follow it (ADR 0049).
*/
const weekdayFormat = cachedDateFormat({
  weekday: 'narrow',
  timeZone: 'UTC',
});
const monthYearFormat = cachedDateFormat({
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const weekdayLongFormat = cachedDateFormat({
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/** `['M', 'T', 'W', 'T', 'F', 'S', 'S']` — Monday first. */
export const weekdayLabels = (): string[] =>
  // 1970-01-05 was a Monday.
  Array.from({ length: DAYS_PER_WEEK }, (_, index) =>
    weekdayFormat().format(Date.parse(epochDayToIso(4 + index))),
  );

/** `'September 2026'` — the grid's header. */
export const formatMonthLabel = (isoDate: string): string =>
  monthYearFormat().format(Date.parse(startOfMonthIso(isoDate)));

/** `'Tuesday, 15 September 2026'` — a day cell's accessible name. */
export const formatDayLabel = (isoDate: string): string =>
  weekdayLongFormat().format(Date.parse(isoDate));

/**
 * Whole-day arithmetic for the panel's keyboard walk. UTC epoch days, never a
 * local `Date` — a DST-at-midnight zone breaks `+1 day` (see lib/epochDay.ts).
 */
export const addDaysIso = (isoDate: string, delta: number): string => {
  const [year, month, day] = isoDate.split('-').map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(year, month - 1, day + delta));
  return shifted.toISOString().slice(0, 10);
};

/** Last day of the month `isoDate` falls in. */
export const endOfMonthIso = (isoDate: string): string =>
  addDaysIso(addMonthsIso(startOfMonthIso(isoDate), 1), -1);
